import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Avatar, Button, EmptyState, Icon, Input, Modal, Skeleton, Tabs } from "../ui";
import { useToast } from "../../context/ToastContext";
import api from "../../services/api";
import ChatIcon from "./ChatIcon";
import PopMenu from "./PopMenu";
import { dayLabel, timeOf } from "./format";

const groupOf = (c) => (c.isPinned ? "ปักหมุด" : dayLabel(c.latestMessageTime || c.createdAt));

/** รายการห้องแชท: แท็บ ปกติ/จัดเก็บ, ค้นหา, จัดกลุ่มตามวัน, เมนู ปักหมุด/จัดเก็บ/เปลี่ยนชื่อ/ลบ, โหลดเพิ่ม */
export default function ChatList({ activeId, full, bump, onRemoved }) {
  const toast = useToast();
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [state, setState] = useState({ items: null, nextCursor: null, error: "" });
  const [more, setMore] = useState(false);
  const [rename, setRename] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // เปลี่ยนแท็บ/คำค้น = โหลดใหม่
  const archived = tab === "archived";
  useEffect(() => {
    let alive = true;
    setState({ items: null, nextCursor: null, error: "" });
    api.chats.listPage({ ...(archived ? { archived: true } : {}), ...(query ? { q: query } : {}) })
      .then((r) => alive && setState({ items: r.items, nextCursor: r.nextCursor, error: "" }))
      .catch((e) => alive && setState((s) => ({ ...s, items: s.items || [], error: e.message })));
    return () => { alive = false; };
  }, [archived, query]);

  // ห้องแชทแจ้งการเปลี่ยนแปลง (ข้อความใหม่/เปลี่ยนชื่อ) -> แก้รายการในหน้าเลย ไม่ต้องเรียก API ซ้ำ
  useEffect(() => {
    if (!bump) return;
    const { id, n, remove, ...p } = bump; // eslint-disable-line no-unused-vars
    setState((s) => (!s.items ? s : remove ? { ...s, items: s.items.filter((c) => c.id !== id) } : {
      ...s,
      items: s.items.map((c) => (c.id === id ? { ...c, ...p } : c))
        .sort((a, b) => Number(b.isPinned) - Number(a.isPinned) || String(b.latestMessageTime || "").localeCompare(String(a.latestMessageTime || ""))),
    }));
  }, [bump]);

  const loadMore = async () => {
    setMore(true);
    try {
      const r = await api.chats.listPage({ ...(archived ? { archived: true } : {}), ...(query ? { q: query } : {}), cursor: state.nextCursor });
      setState((s) => ({ items: [...s.items, ...r.items.filter((x) => !s.items.some((y) => y.id === x.id))], nextCursor: r.nextCursor, error: "" }));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setMore(false);
    }
  };

  const patch = useCallback((id, p) => setState((s) => ({ ...s, items: (s.items || []).map((c) => (c.id === id ? { ...c, ...p } : c)) })), []);
  const drop = useCallback((id) => setState((s) => ({ ...s, items: (s.items || []).filter((c) => c.id !== id) })), []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast(okMsg, "success"); return true; } catch (e) { toast(e.message, "error"); return false; } finally { setBusy(false); }
  };
  const togglePin = (c) => run(async () => { await api.chats.update(c.id, { isPinned: !c.isPinned }); patch(c.id, { isPinned: !c.isPinned }); setState((s) => ({ ...s, items: [...s.items].sort((a, b) => Number(b.isPinned) - Number(a.isPinned)) })); });
  const toggleArchive = (c) => run(async () => { await api.chats.update(c.id, { isArchived: !c.isArchived }); drop(c.id); if (c.id === activeId) onRemoved?.(); }, c.isArchived ? "ย้ายกลับแล้ว" : "จัดเก็บแชทแล้ว");
  const doRename = async () => {
    const name = rename.name.trim();
    if (!name) return;
    if (await run(() => api.chats.update(rename.id, { name }), "เปลี่ยนชื่อแล้ว")) { patch(rename.id, { name }); setRename(null); }
  };
  const doDelete = async () => {
    if (await run(() => api.chats.remove(del.id), "ลบแชทแล้ว")) { drop(del.id); if (del.id === activeId) onRemoved?.(); setDel(null); }
  };

  const items = state.items;
  let lastGroup = "";
  return (
    <aside className={`chat-list${full ? " chat-list--full" : ""}`} aria-label="รายการแชท">
      <div className="row-between mb-8"><h2>แชท</h2><Link to="/" className="btn btn--sm btn--primary"><Icon name="plus" size={16} />แชทใหม่</Link></div>
      <Tabs tabs={[{ value: "active", label: "แชทของฉัน" }, { value: "archived", label: "จัดเก็บแล้ว" }]} value={tab} onChange={setTab} />
      <div className="chat-search mt-8">
        <Icon name="search" size={16} />
        <input placeholder="ค้นหาแชท" aria-label="ค้นหาแชท" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {state.error ? <div className="error-text mb-8" role="alert">{state.error}</div> : null}
      {items === null ? (
        <div className="stack" aria-busy="true">{[0, 1, 2, 3].map((i) => <div key={i} className="row"><Skeleton width={44} height={44} radius="50%" /><div className="grow"><Skeleton height={14} width="60%" /><Skeleton height={11} width="85%" style={{ marginTop: 6 }} /></div></div>)}</div>
      ) : !items.length ? (
        <EmptyState icon={archived ? "🗂️" : "💬"} title={query ? "ไม่พบแชทที่ค้นหา" : archived ? "ยังไม่มีแชทที่จัดเก็บ" : "ยังไม่มีแชท"}>
          {query || archived ? null : "เลือกตัวละครจากหน้าแรกเพื่อเริ่มคุย"}
        </EmptyState>
      ) : items.map((c) => {
        const g = groupOf(c);
        const head = g !== lastGroup ? <div className="chat-list__group" key={`g-${g}-${c.id}`}>{g}</div> : null;
        lastGroup = g;
        return (
          <React.Fragment key={c.id}>
            {head}
            <div className="row chat-item">
              <NavLink to={`/chat/${c.id}`} className={`chat-row grow${activeId === c.id ? " is-active" : ""}`}>
                <Avatar name={c.name} seed={c.characterId} colors={c.colors} src={c.image} size={46} character />
                <div className="grow">
                  <div className="strong clamp-1">{c.isPinned ? <ChatIcon name="pin" size={13} className="pin-ico" /> : null}{c.name}</div>
                  <div className="faint clamp-1" style={{ fontSize: 12 }}>{c.lastPreview ? c.lastPreview.replace(/\*/g, "") : timeOf(c.latestMessageTime)}</div>
                </div>
                <span className="faint" style={{ fontSize: 11 }}>{timeOf(c.latestMessageTime)}</span>
              </NavLink>
              <PopMenu label={`เมนูแชท ${c.name}`} items={[
                { label: c.isPinned ? "เลิกปักหมุด" : "ปักหมุดแชท", icon: "pin", onClick: () => togglePin(c) },
                { label: "เปลี่ยนชื่อแชท", icon: "pencil", onClick: () => setRename({ id: c.id, name: c.name }) },
                { label: c.isArchived ? "ย้ายกลับ" : "จัดเก็บแชท", icon: "archive", onClick: () => toggleArchive(c) },
                { label: "ลบแชท", icon: "trash", danger: true, onClick: () => setDel(c) },
              ]} />
            </div>
          </React.Fragment>
        );
      })}
      {state.nextCursor ? <Button block className="mt-8" onClick={loadMore} disabled={more}>{more ? "กำลังโหลด..." : "โหลดเพิ่ม"}</Button> : null}

      <Modal open={!!rename} title="เปลี่ยนชื่อแชท" size="sm" onClose={() => setRename(null)}
        footer={<><Button onClick={() => setRename(null)}>ยกเลิก</Button><Button variant="primary" disabled={busy || !rename?.name.trim()} onClick={doRename}>บันทึก</Button></>}>
        <Input label="ชื่อแชท" maxLength={40} value={rename?.name || ""} onChange={(e) => setRename((r) => ({ ...r, name: e.target.value }))} hint="ไม่เกิน 40 ตัวอักษร" autoFocus />
      </Modal>
      <Modal open={!!del} title="ลบแชทนี้?" size="sm" onClose={() => setDel(null)}
        footer={<><Button onClick={() => setDel(null)}>ยกเลิก</Button><Button variant="danger" disabled={busy} onClick={doDelete}>ลบแชท</Button></>}>
        <p>แชทกับ <b>{del?.name}</b> จะหายจากรายการของคุณ ย้อนกลับไม่ได้</p>
      </Modal>
    </aside>
  );
}
